import type React from "react";
import Box from "@mui/material/Box";
import {chipTones, colors, fonts, type ChipTone} from "../../theme/tokens";

type StatusChipProps = {
    tone: ChipTone;
    children: React.ReactNode;
    icon?: React.ReactNode;
    mono?: boolean;
    title?: string;
};

/** 20px status pill; colour always pairs with text so state is never conveyed by colour alone. */
export function StatusChip({tone, children, icon, mono, title}: StatusChipProps) {
    const palette = chipTones[tone];
    return (
            <Box
                    component="span"
                    title={title}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        height: 20,
                        px: "7px",
                        borderRadius: "3px",
                        fontSize: "11.5px",
                        fontWeight: 500,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        color: palette.color,
                        backgroundColor: palette.background,
                        fontFamily: mono ? fonts.mono : undefined,
                        "& svg": {fontSize: "14px"},
                    }}
            >
                {icon}
                {children}
            </Box>
    );
}

/** Coloured dot for a row's status; the accessible label carries the meaning. */
export function StatusDot({color, label}: {color: string; label: string}) {
    return (
            <Box
                    component="span"
                    role="img"
                    aria-label={label}
                    title={label}
                    sx={{display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0}}
            />
    );
}

export function MonoText({children, muted}: {children: React.ReactNode; muted?: boolean}) {
    return (
            <Box component="span" sx={{fontFamily: fonts.mono, fontSize: "11.5px", color: muted ? colors.faint : colors.body}}>
                {children}
            </Box>
    );
}

/** Keyboard shortcut hint shown on primary actions; `onDark` for use on filled buttons and the top bar. */
export function KeyHint({children, onDark}: {children: React.ReactNode; onDark?: boolean}) {
    return (
            <Box
                    component="kbd"
                    aria-hidden
                    sx={{
                        fontFamily: fonts.mono,
                        fontSize: "10.5px",
                        fontWeight: 600,
                        px: 0.6,
                        py: 0.1,
                        borderRadius: "3px",
                        backgroundColor: onDark ? "rgba(255,255,255,.18)" : "rgba(97,112,138,.14)",
                        color: "inherit",
                        ml: 0.75,
                        lineHeight: 1.5,
                    }}
            >
                {children}
            </Box>
    );
}
